import { useContext, useEffect } from 'react';
import { debounce } from 'lodash';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  decapitalize,
  formatDisplayName,
  mergePaginatedResults,
} from '~/frontend/shared/utils/helpers';
import { AppContext } from '@/shared/context/context';
import { type Case } from '@/types/cases';
import caseApi from '~/frontend/shared/api/cases/case';
import CaseStatusType from '~/common/enums/caseStatusType';
import { formatCases } from '~/frontend/shared/utils/cases/cases';
import { getNonPendingStatusFilters } from '@/containers/Cases/filters';
import { pagination } from '~/common/constants';
import { queryIds } from '~/frontend/shared/utils/constants';
import signalRMessages from '@/redux/actions/signalRMessages';
import useNotification from '~/frontend/shared/hooks/UseNotification';

const DEBOUNCE_TIMEOUT = 2000;

type AutocompleteOption = {
  value: string;
  label?: string;
};

type UseGetCasesProps = {
  enabled?: boolean;
  formatOptions: {
    currentTeam: unknown;
    currentTeamUser: unknown;
  };
  params: {
    autocomplete?: Record<string, AutocompleteOption | AutocompleteOption[]>;
    input?: {
      addressLine?: string | null;
      city?: string | null;
      country?: string | null;
      postalCode?: string | null;
    };
  };
};

type PaginatedCasesResponse = {
  items: Case[];
  nextPage: number | null;
};

type ScrollMoreOptions = {
  baseFunc: (e: React.UIEvent<HTMLElement>) => void;
};

const enrichQueryParams = ({
  queryParams,
  updatedAutocomplete,
}: {
  queryParams: URLSearchParams;
  updatedAutocomplete?: Record<string, AutocompleteOption | AutocompleteOption[]>;
}) => {
  if (!updatedAutocomplete) return;

  Object.entries(updatedAutocomplete).forEach(([key, autoCompleteParams]) => {
    if (!autoCompleteParams) return;

    if (!Array.isArray(autoCompleteParams)) {
      queryParams.append(key, autoCompleteParams.value);
    } else {
      autoCompleteParams.forEach((param) => {
        queryParams.append(key, param.value);
      });
    }
  });
};

const useGetCases = ({ params, formatOptions, enabled = true }: UseGetCasesProps) => {
  const { socket } = useContext(AppContext);
  const { sendDefaultError } = useNotification();
  const { t } = useTranslation();
  const { autocomplete, ...rest } = params;
  const filterParams = Object.assign({}, ...Object.values(rest));
  const queryParams = new URLSearchParams({ ...filterParams });
  const updatedAutocomplete = {
    ...autocomplete,
    statuses:
      autocomplete?.statuses && Array.isArray(autocomplete.statuses) && autocomplete.statuses.length
        ? autocomplete.statuses.filter((s) =>
            Object.values(CaseStatusType).includes(s.value as CaseStatusType),
          )
        : getNonPendingStatusFilters(t),
  };

  enrichQueryParams({ queryParams, updatedAutocomplete });

  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage,
    isLoading,
    isFetching,
    refetch,
  } = useInfiniteQuery<PaginatedCasesResponse>({
    enabled,

    getNextPageParam: (lastPage) => lastPage.nextPage,

    onError: (error) => {
      sendDefaultError(error);
    },

    queryFn: async ({ pageParam = pagination.queryParam.FIRST_PAGE }) => {
      const { items, nextPage } = await caseApi.getCases({
        page: pageParam,
        params: queryParams.toString(),
      });

      const { currentTeam, currentTeamUser } = formatOptions;

      const formatted = formatCases({
        cases: items,
        currentTeam,
        currentTeamUser,
      });

      return {
        items: formatted,
        nextPage,
      };
    },

    queryKey: [queryIds.cases.GET_CASES, queryParams.toString()],
  });

  const handleScrollMore = (e: React.UIEvent<HTMLElement>, { baseFunc }: ScrollMoreOptions) => {
    baseFunc(e);

    const tableWrapper = e.currentTarget;

    const scrollMore =
      tableWrapper.scrollTop + 1 > tableWrapper.scrollHeight - tableWrapper.offsetHeight;

    if (!isLoading && !isFetchingNextPage && hasNextPage && scrollMore) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    const debouncedRefetch = debounce(
      () => setTimeout(() => refetch(), DEBOUNCE_TIMEOUT),
      DEBOUNCE_TIMEOUT,
      {
        leading: false,
        trailing: true,
      },
    );

    if (!socket) {
      return;
    }

    socket.on(signalRMessages.CaseUpdated, debouncedRefetch);
    socket.on(signalRMessages.CaseRelationAdded, debouncedRefetch);
    socket.on(signalRMessages.CaseRelationUpdated, debouncedRefetch);
    socket.on(signalRMessages.CaseRelationRemoved, debouncedRefetch);

    return () => {
      socket.off(signalRMessages.CaseUpdated, debouncedRefetch);
      socket.off(signalRMessages.CaseRelationAdded, debouncedRefetch);
      socket.off(signalRMessages.CaseRelationUpdated, debouncedRefetch);
      socket.off(signalRMessages.CaseRelationRemoved, debouncedRefetch);
    };
  }, [socket, refetch]);

  const cases = (mergePaginatedResults(data) ?? []) as Case[];

  const casesAutocomplete = cases.map((c: Case) => ({
    label: `${c.reference}${
      c.description && c.description !== '-'
        ? ` | ${c.description.length > 50 ? `${c.description.slice(0, 50)}...` : c.description}`
        : ''
    }${
      c.relations?.filter((r) => r.isRequestor).length
        ? ` | ${c.relations
            .filter((r) => r.isRequestor)
            .map((r) => formatDisplayName(r).valueOf())
            .join(', ')}`
        : ''
    } | ${t(decapitalize(c.status))}`,
    type: c.caseType,
    value: c.id,
  }));

  return {
    cases,
    casesAutocomplete,
    casesIsFetching: isFetching,
    casesIsLoading: isLoading || isFetchingNextPage,
    hasMoreCases: hasNextPage,
    loadMoreCases: fetchNextPage,
    scrollMore: handleScrollMore,
  };
};

export default useGetCases;
